import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Eye, Target, Calendar, TrendingUp, Code, Shield, CheckCircle, AlertCircle, Users } from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend } from 'recharts';

interface ARIAModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ARIAModal({ isOpen, onClose }: ARIAModalProps) {
  const [activeTab, setActiveTab] = useState<'vision' | 'spider' | 'roadmap' | 'vente' | 'programmation' | 'gouvernance'>('vision');

  const tabs = [
    { id: 'vision' as const, label: 'Vision & Esprit', icon: Eye },
    { id: 'spider' as const, label: 'SPIDER – Maturité', icon: Target },
    { id: 'roadmap' as const, label: 'Roadmap 2026', icon: Calendar },
    { id: 'vente' as const, label: 'Vente & ROI', icon: TrendingUp },
    { id: 'programmation' as const, label: 'Programmation & IA', icon: Code },
    { id: 'gouvernance' as const, label: 'Gouvernance (RACI)', icon: Shield },
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl w-[90vw] h-[90vh] border border-indigo-500/30 shadow-2xl shadow-indigo-500/20 flex flex-col">
              {/* Header */}
              <div className="relative p-4 border-b border-indigo-500/30 bg-gradient-to-r from-indigo-900/50 to-purple-900/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg">
                      <Eye className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                        ARIA
                      </h2>
                      <p className="text-gray-400 text-sm">
                        Moteur d'intelligence contextuelle de BZ
                      </p>
                      <div className="flex gap-1.5 mt-1.5">
                        <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-300 text-[10px] rounded-full border border-indigo-500/30">IA</span>
                        <span className="px-2 py-0.5 bg-purple-500/20 text-purple-300 text-[10px] rounded-full border border-purple-500/30">MCP</span>
                        <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-[10px] rounded-full border border-blue-500/30">Data</span>
                        <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-[10px] rounded-full border border-emerald-500/30">Premium</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Tabs Navigation */}
              <div className="border-b border-indigo-500/30 bg-slate-900/50 px-8 pt-4 pb-0 overflow-x-auto">
                <div className="flex gap-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-6 py-3 rounded-t-lg font-semibold transition-all whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'bg-gradient-to-b from-indigo-600 to-purple-600 text-white shadow-lg'
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-8 bg-gradient-to-b from-slate-900 to-slate-950">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {activeTab === 'vision' && <VisionTab />}
                    {activeTab === 'spider' && <SpiderTab />}
                    {activeTab === 'roadmap' && <RoadmapTab />}
                    {activeTab === 'vente' && <VenteTab />}
                    {activeTab === 'programmation' && <ProgrammationTab />}
                    {activeTab === 'gouvernance' && <GouvernanceTab />}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-indigo-500/30 bg-slate-900/50">
                <div className="flex items-center justify-end">
                  <button
                    onClick={onClose}
                    className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all duration-200 font-semibold shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/40 hover:scale-105"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ============================================================================
// ONGLET A — Vision & Esprit
// ============================================================================
function VisionTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-8 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-4">
          Vision & Esprit ARIA
        </h3>
        <p className="text-gray-300 text-center text-lg">
          Le moteur d'intelligence contextuelle qui transforme l'accès aux données en conversation naturelle
        </p>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Target className="w-6 h-6 text-indigo-400" />
          Mission
        </h4>
        <p className="text-gray-300 leading-relaxed text-lg">
          ARIA est le moteur d'intelligence contextuelle de BZ. Elle permet à un humain non-technique de dialoguer avec les données de l'entreprise en langage naturel, tout en conservant un contrôle humain total.
        </p>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-purple-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Eye className="w-6 h-6 text-purple-400" />
          Vision
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>L'IA ne décide jamais, elle propose</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>L'humain reste maître en tout temps</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>La valeur vient de la compréhension métier, pas de la magie IA</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>ARIA est invisible, mais essentielle (comme ChatGPT pour PRIsME)</span>
          </li>
        </ul>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-blue-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-blue-400" />
          Positionnement
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <span>Framework interne BZ</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <span>Module premium PRIsME</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <span>UI simple + API backend</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <span>Autonomie complète du end-user, sans dépendance à un programmeur</span>
          </li>
        </ul>
      </div>

      <div className="bg-red-500/10 rounded-xl p-6 border border-red-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <AlertCircle className="w-6 h-6 text-red-400" />
          ARIA n'est pas
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <span>Un chatbot libre</span>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <span>Une IA autonome</span>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <span>Une boîte noire</span>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <span>Un outil réservé aux développeurs</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET B — SPIDER (AS-IS / TARGET)
// ============================================================================
function SpiderTab() {
  const spiderData = [
    { pilier: 'Sécurité & conformité', asIs: 2, target: 4 },
    { pilier: 'Gouvernance IA', asIs: 1, target: 4 },
    { pilier: 'Architecture', asIs: 3, target: 4 },
    { pilier: 'Qualité des réponses', asIs: 3, target: 4 },
    { pilier: 'Compréhension métier', asIs: 2, target: 4 },
    { pilier: 'UX', asIs: 1, target: 3 },
    { pilier: 'Workflow humain', asIs: 2, target: 4 },
    { pilier: 'Performance', asIs: 2, target: 3 },
    { pilier: 'Amélioration continue', asIs: 1, target: 3 },
    { pilier: 'Intégration PRIsME', asIs: 1, target: 4 },
    { pilier: 'Communication & adoption', asIs: 1, target: 3 },
    { pilier: 'Commercialisation', asIs: 0, target: 3 },
  ];

  // Convertir pour le graphique radar (échelle 0-5 vers 0-100)
  const chartData = spiderData.map(item => ({
    category: item.pilier.length > 20 ? item.pilier.substring(0, 20) + '...' : item.pilier,
    ASIS: (item.asIs / 5) * 100,
    Target: (item.target / 5) * 100,
  }));

  const asisAverage = Math.round(
    spiderData.reduce((sum, item) => sum + item.asIs, 0) / spiderData.length
  );
  const targetAverage = Math.round(
    spiderData.reduce((sum, item) => sum + item.target, 0) / spiderData.length
  );

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          SPIDER – Maturité (AS-IS / TARGET)
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Évaluation de la maturité actuelle et cible pour fin 2026
        </p>
      </div>

      {/* Scores moyens */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4">
          <div className="text-sm text-yellow-300 font-medium">Score ASIS (Actuel)</div>
          <div className="text-2xl font-bold text-yellow-200">{asisAverage}/5</div>
          <div className="text-xs text-yellow-400">État actuel</div>
        </div>
        <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-4">
          <div className="text-sm text-emerald-300 font-medium">Score Target (Objectif)</div>
          <div className="text-2xl font-bold text-emerald-200">{targetAverage}/5</div>
          <div className="text-xs text-emerald-400">Excellence visée</div>
        </div>
      </div>

      {/* Graphique Spider */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4">Graphique Radar - Maturité ARIA</h4>
        <div className="h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
              <PolarGrid stroke="#4b5563" />
              <PolarAngleAxis 
                dataKey="category" 
                tick={{ fontSize: 11, fill: '#d1d5db' }}
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 100]} 
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickCount={6}
              />
              
              {/* Ligne ASIS (État actuel) */}
              <Radar
                name="ASIS (Actuel)"
                dataKey="ASIS"
                stroke="#fbbf24"
                fill="#fbbf24"
                fillOpacity={0.2}
                strokeWidth={3}
                dot={{ fill: '#fbbf24', strokeWidth: 2, r: 4 }}
              />
              
              {/* Ligne Target (Objectif) */}
              <Radar
                name="Target (Objectif)"
                dataKey="Target"
                stroke="#10b981"
                fill="#10b981"
                fillOpacity={0.1}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
              />
              
              <Legend 
                wrapperStyle={{ paddingTop: '20px', color: '#d1d5db' }}
                iconType="circle"
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Échelle et contexte */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <h4 className="text-lg font-bold text-white mb-3">Échelle de maturité</h4>
          <ul className="space-y-2 text-gray-300 text-sm">
            <li className="flex items-center gap-2">
              <span className="font-semibold text-indigo-400">1 =</span>
              <span>POC</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="font-semibold text-indigo-400">3 =</span>
              <span>Solide en usage interne</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="font-semibold text-indigo-400">5 =</span>
              <span>Produit maîtrisé</span>
            </li>
          </ul>
        </div>
        <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30">
          <h4 className="text-lg font-bold text-white mb-2">État actuel (AS-IS)</h4>
          <p className="text-gray-300 text-sm leading-relaxed">
            ARIA est techniquement solide, mais organisationnellement immature. La priorité est la gouvernance, l'UX et la structuration produit.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET C — Roadmap 2026
// ============================================================================
function RoadmapTab() {
  const [viewMode, setViewMode] = useState<'quarters' | 'timeline'>('quarters');

  const quarters = [
    {
      quarter: 'Q1 2026 — Fondations',
      items: [
        'Formalisation du MCP',
        'Sécurité & audit trail complet',
        'Gouvernance IA documentée et appliquée',
        'Mode lecture et simulation',
        'Définition du Semantic Layer',
      ],
      resultat: 'ARIA fiable',
      color: 'indigo',
      months: ['Jan', 'Fév', 'Mar'],
    },
    {
      quarter: 'Q2 2026 — Autonomie utilisateur',
      items: [
        'UI simple',
        'Validation humaine systématique',
        'Visualisation de la compréhension IA',
        'Rôles PRIsME appliqués',
        'Documentation fonctionnelle',
      ],
      resultat: 'Autonomie sans programmeur',
      color: 'purple',
      months: ['Avr', 'Mai', 'Juin'],
    },
    {
      quarter: 'Q3 2026 — Valeur mesurable',
      items: [
        'Cas d\'usage PRIsME (finance, ops, RH)',
        'KPI: temps gagné, erreurs évitées',
        'Amélioration performance et stabilité',
        'Formation interne structurée',
        'Préparation pitch ventes',
      ],
      resultat: 'Avantage compétitif démontré',
      color: 'blue',
      months: ['Juil', 'Août', 'Sep'],
    },
    {
      quarter: 'Q4 2026 — Produit premium',
      items: [
        'Packaging module premium PRIsME',
        'Onboarding client type',
        'Démonstrateurs clients',
        'Encadrement contractuel et limites',
        'Décision stratégique pour 2027',
      ],
      resultat: 'Produit commercialisable',
      color: 'emerald',
      months: ['Oct', 'Nov', 'Déc'],
    },
  ];

  // Vue mensuelle linéaire
  const monthlyTimeline = [
    { month: 'Jan 2026', quarter: 'Q1', items: ['Formalisation du MCP'], color: 'indigo' },
    { month: 'Fév 2026', quarter: 'Q1', items: ['Sécurité & audit trail'], color: 'indigo' },
    { month: 'Mar 2026', quarter: 'Q1', items: ['Gouvernance IA', 'Mode lecture'], color: 'indigo' },
    { month: 'Avr 2026', quarter: 'Q2', items: ['UI simple'], color: 'purple' },
    { month: 'Mai 2026', quarter: 'Q2', items: ['Validation humaine', 'Visualisation IA'], color: 'purple' },
    { month: 'Juin 2026', quarter: 'Q2', items: ['Rôles PRIsME', 'Documentation'], color: 'purple' },
    { month: 'Juil 2026', quarter: 'Q3', items: ['Cas d\'usage PRIsME'], color: 'blue' },
    { month: 'Août 2026', quarter: 'Q3', items: ['KPI & métriques'], color: 'blue' },
    { month: 'Sep 2026', quarter: 'Q3', items: ['Formation', 'Pitch ventes'], color: 'blue' },
    { month: 'Oct 2026', quarter: 'Q4', items: ['Packaging premium'], color: 'emerald' },
    { month: 'Nov 2026', quarter: 'Q4', items: ['Onboarding', 'Démonstrateurs'], color: 'emerald' },
    { month: 'Déc 2026', quarter: 'Q4', items: ['Décision stratégique 2027'], color: 'emerald' },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Roadmap 2026
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Planification trimestrielle et mensuelle pour transformer ARIA en produit premium
        </p>
      </div>

      {/* Toggle vue */}
      <div className="flex gap-2 justify-center">
        <button
          onClick={() => setViewMode('quarters')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            viewMode === 'quarters'
              ? 'bg-indigo-600 text-white'
              : 'bg-white/5 text-gray-400 hover:bg-white/10'
          }`}
        >
          Vue Trimestrielle
        </button>
        <button
          onClick={() => setViewMode('timeline')}
          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
            viewMode === 'timeline'
              ? 'bg-indigo-600 text-white'
              : 'bg-white/5 text-gray-400 hover:bg-white/10'
          }`}
        >
          Vue Mensuelle Linéaire
        </button>
      </div>

      {viewMode === 'quarters' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {quarters.map((q, idx) => {
            const colorClasses = {
              indigo: 'from-indigo-500/20 to-indigo-600/20 border-indigo-500/30',
              purple: 'from-purple-500/20 to-purple-600/20 border-purple-500/30',
              blue: 'from-blue-500/20 to-blue-600/20 border-blue-500/30',
              emerald: 'from-emerald-500/20 to-emerald-600/20 border-emerald-500/30',
            };
            const bgClass = colorClasses[q.color as keyof typeof colorClasses];
            
            return (
              <div key={idx} className={`bg-gradient-to-br ${bgClass} rounded-xl p-6 border-2`}>
                <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-6 h-6" />
                  {q.quarter}
                </h4>
                <ul className="space-y-2 mb-4">
                  {q.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex items-start gap-2 text-gray-300">
                      <CheckCircle className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-1" />
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-sm font-semibold text-white">
                    Résultat attendu: <span className="text-indigo-300">{q.resultat}</span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
          {/* Timeline linéaire verticale */}
          <div className="relative">
            {/* Ligne verticale centrale */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-indigo-500/30"></div>
            
            <div className="space-y-6">
              {monthlyTimeline.map((item, idx) => {
                const colorMap = {
                  indigo: 'bg-indigo-500/20 border-indigo-500/50 text-indigo-300',
                  purple: 'bg-purple-500/20 border-purple-500/50 text-purple-300',
                  blue: 'bg-blue-500/20 border-blue-500/50 text-blue-300',
                  emerald: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300',
                };
                const colorClass = colorMap[item.color as keyof typeof colorMap];
                
                return (
                  <div key={idx} className="relative flex items-start gap-4">
                    {/* Point sur la ligne */}
                    <div className="relative z-10 flex-shrink-0">
                      <div className={`w-4 h-4 rounded-full border-2 ${colorClass.split(' ')[1]} bg-slate-900`}></div>
                    </div>
                    
                    {/* Contenu */}
                    <div className="flex-1">
                      <div className={`${colorClass} rounded-lg p-4 border-2 mb-2`}>
                        <div className="font-bold text-sm mb-1">{item.month}</div>
                        <div className="text-xs opacity-75">{item.quarter}</div>
                      </div>
                      <ul className="space-y-1">
                        {item.items.map((task, taskIdx) => (
                          <li key={taskIdx} className="text-xs text-gray-300 flex items-start gap-1">
                            <CheckCircle className="w-3 h-3 text-indigo-400 flex-shrink-0 mt-0.5" />
                            <span className="leading-tight">{task}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ONGLET D — Vente & ROI
// ============================================================================
function VenteTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-8 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-4">
          Vente & ROI
        </h3>
        <p className="text-gray-300 text-center">
          Proposition de valeur et retour sur investissement
        </p>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-indigo-400" />
          Proposition de valeur
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Réduction du temps d'analyse</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Réduction des erreurs humaines</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Accélération de la prise de décision</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Autonomie des équipes non-tech</span>
          </li>
        </ul>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-xl p-6 border border-indigo-500/30">
          <h4 className="text-lg font-bold text-white mb-3">Pitch exécutif</h4>
          <p className="text-gray-300 italic text-sm leading-relaxed">
            "ARIA permet à vos équipes de poser les bonnes questions à leurs données, sans risque et sans dépendre d'un programmeur."
          </p>
        </div>
        <div className="bg-gradient-to-br from-blue-500/20 to-indigo-500/20 rounded-xl p-6 border border-blue-500/30">
          <h4 className="text-lg font-bold text-white mb-3">Pitch TI</h4>
          <p className="text-gray-300 italic text-sm leading-relaxed">
            "ARIA structure l'accès aux données par gouvernance IA, pas par magie."
          </p>
        </div>
        <div className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl p-6 border border-emerald-500/30">
          <h4 className="text-lg font-bold text-white mb-3">Pitch CFO / Ops</h4>
          <p className="text-gray-300 italic text-sm leading-relaxed">
            "Moins de rapports manuels, moins d'erreurs, plus de visibilité."
          </p>
        </div>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-purple-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Target className="w-6 h-6 text-purple-400" />
          ROI / KPI suggérés
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
            <span>Heures analyste économisées par mois</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
            <span>Nombre d'erreurs évitées</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
            <span>Délai moyen pour obtenir un indicateur (avant / après)</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
            <span>Taux d'adoption hebdomadaire</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET E — Programmation & IA
// ============================================================================
function ProgrammationTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-8 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-4">
          Programmation & IA
        </h3>
        <p className="text-gray-300 text-center">
          Principes techniques et architecture
        </p>
      </div>

      <div className="bg-red-500/10 rounded-xl p-6 border border-red-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <AlertCircle className="w-6 h-6 text-red-400" />
          Principes non négociables
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <span>Aucun write sans validation humaine explicite</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <span>Audit trail complet (question, interprétation, requête, décision, résultat)</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <span>Règles explicites &gt; prompt magique</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <span>Réponses structurées, concises et justifiées</span>
          </li>
        </ul>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Code className="w-6 h-6 text-indigo-400" />
          Architecture logique
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>ARIA Core Engine</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Connecteurs bases de données</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Semantic Layer métier</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Governance Layer</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>UI utilisateur + API</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Logs & monitoring</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET F — Gouvernance (RACI)
// ============================================================================
function GouvernanceTab() {
  const getRoleColor = (code: string) => {
    if (code.includes('A')) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (code.includes('R')) return 'bg-red-100 text-red-800 border-red-300';
    if (code.includes('C')) return 'bg-green-100 text-green-800 border-green-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const raciData = [
    { role: 'Marc-Antoine Caron (Marco)', responsabilite: 'Product Owner / Owner stratégique / Moteur IA / MCP', code: 'A/R', avatar: '/MAC.png' },
    { role: 'Éric Durand', responsabilite: 'Architecture et qualité technique', code: 'R', avatar: '/ED.png' },
    { role: 'Dany Bourassa', responsabilite: 'Intégration PRIsME et gouvernance globale', code: 'R', avatar: '/DB.png' },
    { role: 'Antoine Giroux', responsabilite: 'Packaging, pitch, feedback clients', code: 'R', avatar: '/AG-transparent.png' },
    { role: 'Rosalie Emond', responsabilite: 'Positionnement, storytelling, adoption', code: 'R', avatar: '/RE.png' },
    { role: 'Utilisateurs clés BZ', responsabilite: 'Feedback et amélioration continue', code: 'C', avatar: null },
  ];

  // Activités pour la matrice RACI
  const activities = [
    { 
      name: 'Définition stratégique', 
      description: 'Vision produit et roadmap',
      roles: {
        'Marc-Antoine Caron (Marco)': 'R',
        'Éric Durand': 'A',
      }
    },
    { 
      name: 'Développement moteur IA', 
      description: 'Core engine et MCP',
      roles: {
        'Marc-Antoine Caron (Marco)': 'R',
        'Éric Durand': 'A',
      }
    },
    { 
      name: 'Architecture technique', 
      description: 'Fondations et qualité',
      roles: {
        'Marc-Antoine Caron (Marco)': 'R',
        'Éric Durand': 'A',
      }
    },
    { 
      name: 'Intégration PRIsME', 
      description: 'Gouvernance et intégration',
      roles: {
        'Marc-Antoine Caron (Marco)': 'R',
        'Dany Bourassa': 'A',
        'Éric Durand': 'C',
      }
    },
    { 
      name: 'Packaging commercial', 
      description: 'Pitch et démonstrateurs',
      roles: {
        'Rosalie Emond': 'R',
        'Antoine Giroux': 'R',
        'Éric Durand': 'A',
      }
    },
    { 
      name: 'Communication & adoption', 
      description: 'Storytelling et formation',
      roles: {
        'Rosalie Emond': 'R',
        'Antoine Giroux': 'R',
        'Éric Durand': 'A',
      }
    },
    { 
      name: 'Feedback utilisateurs', 
      description: 'Amélioration continue',
      roles: {
        'Marc-Antoine Caron (Marco)': 'R',
        'Éric Durand': 'A',
        'Utilisateurs clés BZ': 'C',
      }
    },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Gouvernance (RACI)
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Règles de gouvernance et matrice RACI
        </p>
      </div>

      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-indigo-400" />
          Règles de gouvernance
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>IA assistée uniquement (jamais autonome)</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Validation humaine obligatoire</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Traçabilité complète</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Gestion fine des rôles et du scope</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Risques et limites documentés</span>
          </li>
        </ul>
      </div>

      {/* Légende RACI */}
      <div className="bg-white/5 rounded-xl p-6 border border-purple-500/20">
        <h4 className="text-lg font-bold text-white mb-4">Légende RACI</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="flex items-center gap-2 p-2 bg-red-50/10 rounded border border-red-500/30">
            <div className="w-6 h-6 bg-red-500 text-white text-xs font-bold rounded flex items-center justify-center">R</div>
            <div>
              <div className="text-sm font-medium text-red-300">Responsable</div>
              <div className="text-xs text-red-400">Exécute l'activité</div>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 bg-blue-50/10 rounded border border-blue-500/30">
            <div className="w-6 h-6 bg-blue-500 text-white text-xs font-bold rounded flex items-center justify-center">A</div>
            <div>
              <div className="text-sm font-medium text-blue-300">Imputable</div>
              <div className="text-xs text-blue-400">Responsable du résultat</div>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 bg-green-50/10 rounded border border-green-500/30">
            <div className="w-6 h-6 bg-green-500 text-white text-xs font-bold rounded flex items-center justify-center">C</div>
            <div>
              <div className="text-sm font-medium text-green-300">Consulté</div>
              <div className="text-xs text-green-400">Peut être sollicité</div>
            </div>
          </div>
          <div className="flex items-center gap-2 p-2 bg-gray-50/10 rounded border border-gray-500/30">
            <div className="w-6 h-6 bg-gray-500 text-white text-xs font-bold rounded flex items-center justify-center">I</div>
            <div>
              <div className="text-sm font-medium text-gray-300">Informé</div>
              <div className="text-xs text-gray-400">Tenus au courant</div>
            </div>
          </div>
        </div>
      </div>

      {/* Rôles avec avatars */}
      <div className="bg-white/5 rounded-xl p-6 border border-purple-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Users className="w-6 h-6 text-purple-400" />
          Rôles & Responsabilités
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {raciData.map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 bg-white/5 rounded-lg border border-purple-500/20">
              {item.avatar && (
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-purple-500/30 flex-shrink-0">
                  <img 
                    src={item.avatar} 
                    alt={item.role}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      // Fallback si l'avatar n'existe pas
                      (e.target as HTMLImageElement).src = '/PrismeLogo.png';
                    }}
                  />
                </div>
              )}
              <div className="flex-1">
                <div className="font-semibold text-white text-sm">{item.role}</div>
                <div className="text-xs text-gray-400">{item.responsabilite}</div>
              </div>
              <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full border-2 font-bold text-xs ${getRoleColor(item.code)}`}>
                {item.code}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Matrice RACI visuelle */}
      <div className="bg-white/5 rounded-xl p-6 border border-purple-500/20 overflow-x-auto">
        <h4 className="text-xl font-bold text-white mb-4">Matrice RACI - Activités</h4>
        <table className="w-full border-collapse min-w-full">
          <thead>
            <tr className="border-b border-purple-500/30">
              <th className="text-left p-3 text-white font-semibold min-w-[200px]">Activités</th>
              {raciData.map((role) => (
                <th key={role.role} className="text-center p-3 text-white font-semibold text-xs min-w-[100px]">
                  {role.role.split(' ')[0]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activities.map((activity, idx) => (
              <tr key={idx} className="border-b border-purple-500/10 hover:bg-white/5">
                <td className="p-3 text-gray-300">
                  <div className="font-medium text-sm">{activity.name}</div>
                  <div className="text-xs text-gray-500">{activity.description}</div>
                </td>
                {raciData.map((role) => {
                  const code = activity.roles && role.role in activity.roles ? activity.roles[role.role as keyof typeof activity.roles] : '';
                  
                  return (
                    <td key={role.role} className="text-center p-3">
                      {code && (
                        <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full border-2 font-bold text-xs ${getRoleColor(code)}`}>
                          {code}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

